/**
 * API Blob Storage Service
 * 
 * Implementation of IBlobStorageService that uses the application API.
 * This is the default when no external blob storage is configured.
 * Uses row-based pagination for efficient server-side processing.
 */

import type {
  BlobDownloadOptions,
  BlobMetadata,
  ChunkDownloadResult,
  IBlobStorageService
} from './types';

// API chunk sizes - larger because each request causes server-side work
const API_DEFAULT_FIRST_BATCH_ROWS = 10000;
const API_DEFAULT_BATCH_ROWS = 100000;
const API_DEFAULT_CONCURRENCY = 4;

/**
 * API-based storage service implementation.
 * Uses row-based pagination which is most efficient for API endpoints
 * (server only queries and serializes requested rows).
 */
export class ApiBlobStorageService implements IBlobStorageService {
  readonly provider = 'api' as const;

  private firstBatchRows: number;
  private batchRows: number;
  private defaultConcurrency: number;

  constructor(options?: { firstBatchRows?: number; batchRows?: number; maxConcurrency?: number; }) {
    this.firstBatchRows = options?.firstBatchRows ?? API_DEFAULT_FIRST_BATCH_ROWS;
    this.batchRows = options?.batchRows ?? API_DEFAULT_BATCH_ROWS;
    this.defaultConcurrency = options?.maxConcurrency ?? API_DEFAULT_CONCURRENCY;
  }

  async getMetadata(url: string): Promise<BlobMetadata | null> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers: {
          'Accept': 'application/x-memorypack',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HEAD request failed: ${response.status} ${response.statusText}`);
      }

      const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
      const contentType = response.headers.get('Content-Type') || undefined;
      const etag = response.headers.get('ETag') || undefined;

      return {
        url,
        contentLength,
        contentType,
        etag,
      };
    } catch (error) {
      console.error('[ApiBlobStorage] Failed to get metadata:', error);
      return null;
    }
  }

  async downloadRange(url: string, startByte: number, endByte: number): Promise<ChunkDownloadResult> {
    // For API, we use Range header but it's less efficient than row pagination
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/x-memorypack',
        'Range': `bytes=${startByte}-${endByte}`,
      },
      cache: 'no-store',
    });

    if (!response.ok && response.status !== 206) {
      throw new Error(`Range request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.arrayBuffer();

    let totalSize: number | undefined;
    let isLastChunk = false;

    const contentRange = response.headers.get('Content-Range');
    if (contentRange) {
      const match = contentRange.match(/bytes\s+(\d+)-(\d+)\/(\d+)/);
      if (match) {
        const rangeEnd = parseInt(match[2], 10);
        totalSize = parseInt(match[3], 10);
        isLastChunk = rangeEnd >= totalSize - 1;
      }
    } else if (response.status === 200) {
      totalSize = data.byteLength;
      isLastChunk = true;
    }

    return {
      data,
      startByte,
      endByte: startByte + data.byteLength - 1,
      totalSize,
      isLastChunk,
    };
  }

  async downloadBlob(url: string, options?: BlobDownloadOptions): Promise<ArrayBuffer> {
    const maxConcurrency = options?.maxConcurrency ?? this.defaultConcurrency;
    const onProgress = options?.onProgress;
    const signal = options?.signal;

    console.log(`[ApiBlobStorage] Starting row-pagination download: ${url}`);
    console.log(`[ApiBlobStorage] First batch: ${this.firstBatchRows}, subsequent: ${this.batchRows}, concurrency: ${maxConcurrency}`);

    onProgress?.({
      phase: 'initializing',
      totalBytes: 0,
      bytesDownloaded: 0,
      currentChunk: 0,
      totalChunks: 0,
      percent: 0,
    });

    // First request to get total count
    const firstResult = await this.downloadRowRange(url, 0, this.firstBatchRows - 1);
    const totalRows = firstResult.totalRows;

    if (totalRows === 0) {
      console.log(`[ApiBlobStorage] No rows to download`);
      onProgress?.({
        phase: 'complete',
        totalBytes: firstResult.data.byteLength,
        bytesDownloaded: firstResult.data.byteLength,
        currentChunk: 1,
        totalChunks: 1,
        percent: 100,
      });
      return firstResult.data;
    }

    // Calculate batches
    const remainingRows = totalRows - this.firstBatchRows;
    const remainingBatches = remainingRows > 0 ? Math.ceil(remainingRows / this.batchRows) : 0;
    const totalBatches = 1 + remainingBatches;

    console.log(`[ApiBlobStorage] Total: ${totalRows} rows in ${totalBatches} batches`);

    // Store results in order
    const batchResults: ArrayBuffer[] = new Array(totalBatches);
    batchResults[0] = firstResult.data;

    let completedBatches = 1;
    let rowsDownloaded = Math.min(this.firstBatchRows, totalRows);

    onProgress?.({
      phase: 'downloading',
      totalBytes: totalRows,
      bytesDownloaded: rowsDownloaded,
      currentChunk: completedBatches,
      totalChunks: totalBatches,
      percent: Math.round((rowsDownloaded / totalRows) * 100),
    });

    if (totalBatches === 1) {
      onProgress?.({
        phase: 'complete',
        totalBytes: totalRows,
        bytesDownloaded: rowsDownloaded,
        currentChunk: 1,
        totalChunks: 1,
        percent: 100,
      });
      return batchResults[0];
    }

    // Download remaining batches in parallel
    const downloadBatch = async (batchIndex: number): Promise<void> => {
      if (signal?.aborted) {
        throw new Error('Download aborted');
      }

      const startRow = this.firstBatchRows + (batchIndex - 1) * this.batchRows;
      const endRow = Math.min(startRow + this.batchRows - 1, totalRows - 1);

      const result = await this.downloadRowRange(url, startRow, endRow);
      batchResults[batchIndex] = result.data;
      completedBatches++;
      rowsDownloaded += (result.endRow - result.startRow + 1);

      const percent = Math.round((rowsDownloaded / totalRows) * 100);
      onProgress?.({
        phase: 'downloading',
        totalBytes: totalRows,
        bytesDownloaded: rowsDownloaded,
        currentChunk: completedBatches,
        totalChunks: totalBatches,
        percent,
      });
    };

    // Process with concurrency limit
    const pendingPromises: Promise<void>[] = [];
    let nextBatch = 1;

    const startNextBatch = (): Promise<void> | null => {
      if (nextBatch >= totalBatches) return null;
      const batchIndex = nextBatch++;
      return downloadBatch(batchIndex);
    };

    for (let i = 0; i < Math.min(maxConcurrency, totalBatches - 1); i++) {
      const promise = startNextBatch();
      if (promise) pendingPromises.push(promise);
    }

    while (pendingPromises.length > 0) {
      if (signal?.aborted) {
        throw new Error('Download aborted');
      }

      const completedIndex = await Promise.race(
        pendingPromises.map((p, i) => p.then(() => i))
      );
      pendingPromises.splice(completedIndex, 1);

      const nextPromise = startNextBatch();
      if (nextPromise) {
        pendingPromises.push(nextPromise);
      }
    }

    console.log(`[ApiBlobStorage] Download complete: ${rowsDownloaded} rows in ${totalBatches} batches`);

    onProgress?.({
      phase: 'complete',
      totalBytes: totalRows,
      bytesDownloaded: rowsDownloaded,
      currentChunk: totalBatches,
      totalChunks: totalBatches,
      percent: 100,
    });

    return this.combineArrayBuffers(batchResults);
  }

  async exists(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        headers: {
          'Accept': 'application/x-memorypack',
        },
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private async downloadRowRange(
    url: string,
    startRow: number,
    endRow: number
  ): Promise<{ data: ArrayBuffer; startRow: number; endRow: number; totalRows: number; }> {
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/x-memorypack',
        'X-Row-Range': `${startRow}-${endRow}`,
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Row range request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.arrayBuffer();

    // Parse X-Content-Row-Range: start-end/total
    let totalRows = 0;
    let actualStart = startRow;
    let actualEnd = endRow;

    const rowRangeHeader = response.headers.get('X-Content-Row-Range');
    if (rowRangeHeader) {
      const match = rowRangeHeader.match(/(\d+)-(\d+)\/(\d+)/);
      if (match) {
        actualStart = parseInt(match[1], 10);
        actualEnd = parseInt(match[2], 10);
        totalRows = parseInt(match[3], 10);
      }
    }

    // Fallback to X-Total-Count header
    if (totalRows === 0) {
      const totalCountHeader = response.headers.get('X-Total-Count');
      if (totalCountHeader) {
        totalRows = parseInt(totalCountHeader, 10);
      }
    }

    return { data, startRow: actualStart, endRow: actualEnd, totalRows };
  }

  private combineArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
    const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const buffer of buffers) {
      result.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }

    return result.buffer;
  }
}

